<template>
  <div class="page-shell">

    <AppTab v-model="activeTab" :items="tabs" :columns="3" />

    <div>
      <AppTextField v-model="searchQuery" prepend-inner-icon="mdi-magnify" hide-details placeholder="Search..." />
    </div>
    <p class="result-info">
      {{ pagination.total > 0 ? `${inquiries.length} of ${pagination.total} results` : `${inquiries.length} results` }}
    </p>
    <p v-if="errorMessage" class="result-error">{{ errorMessage }}</p>
    <div v-if="isLoading" class="fetching-state">
      <LoadingState :loading="isLoading" message="Loading inquiries..." :spinner-size="24" :spinner-width="3" />
    </div>
    <div class="inquiry-list" v-else>
      <AppCard v-for="item in inquiries" :key="item.id" class="inquiry-card" unstyled role="button" tabindex="0"
        @click="handleOpen(item.id)">
        <div class="card-left">
          <div class="card-icon" :class="statusStyle(item.status).iconTone">
            <v-avatar v-if="item.customer.avatar" size="42">
              <v-img :src="item.customer.avatar" :alt="item.customer.name" cover />
            </v-avatar>
            <v-icon v-else size="20">mdi-account-circle-outline</v-icon>
          </div>
          <div class="card-copy">
            <p v-if="item.inquiry_no" class="card-id">{{ item.inquiry_no }}</p>
            <p class="card-title">{{ item.customer.name }}</p>
            <div class="card-address">
              <v-icon size="14" class="card-address__icon">mdi-map-marker-outline</v-icon>
              <span class="card-address__text">{{ item.address }}</span>
            </div>
            <div class="card-meta">
              <span>{{ item.date }}</span>
            </div>
          </div>
        </div>
        <span class="card-status" :class="statusClass(item.status)">
          {{ item.status }}
        </span>
      </AppCard>
    </div>
    <div class="text-center">
      <AppButtonLoadMore v-if="hasMore" :loading="isLoadingMore" @click="loadMore">
        Load more
        <v-icon size="18">mdi-chevron-double-down</v-icon>
      </AppButtonLoadMore>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppTab from "@/shared/components/ui/AppTab.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppCard from "@/shared/components/ui/AppCard.vue";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import { fetchVendorInquiries } from "@/shared/services/inquiries.api";
import AppButtonLoadMore from "@/shared/components/ui/AppButtonLoadMore.vue";
import { isVendorMode } from "@/shared/utils/appMode";

const tabs = [
  { label: "New", value: "new" },
  { label: "Quoted", value: "quoted" },
  { label: "Accepted", value: "accepted" },
];
const activeTab = ref("new");
const searchQuery = ref("");
const isLoading = ref(false);
const isLoadingMore = ref(false);
const errorMessage = ref("");
const router = useRouter();

type InquiryItem = {
  id: number;
  inquiry_no: string;
  date: string;
  quotes_count: number;
  status: string;
  customer: {
    name: string;
    avatar: string;
  };
  address: string;
};

type InquiryApiRecord = Record<string, unknown>;
type InquiryQueryParams = Record<string, string>;
type InquiryListMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

const inquiries = ref<InquiryItem[]>([]);
const pagination = ref<InquiryListMeta>({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});
const hasMore = ref(false);
let loadRequestId = 0;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const statusStyleMap: Record<string, { icon: string; iconTone: string }> = {
  new: { icon: "mdi-bell-outline", iconTone: "card-icon--sky" },
  quoted: { icon: "mdi-file-document-outline", iconTone: "card-icon--peach" },
  accepted: { icon: "mdi-check-decagram-outline", iconTone: "card-icon--mint" },
  completed: { icon: "mdi-check-circle-outline", iconTone: "card-icon--amber" },
};

function statusStyle(status: string) {
  const raw = (status || "").toString().trim().toLowerCase();
  return statusStyleMap[raw] || statusStyleMap.new;
}

function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function asNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function normalizeInquiry(item: InquiryApiRecord): InquiryItem {
  return {
    id: asNumber(item.id, 0),
    inquiry_no: asString(item.inquiry_no, "-"),
    date: asString(item.date || item.created_at || item.updated_at, "-"),
    quotes_count: asNumber(item.quotes_count ?? 0, 0),
    status: asString(item.status, "new"),
    customer: {
      name: asString((item.customer as any)?.name ?? (item as any)?.customer_name, "-"),
      avatar: asString((item.customer as any)?.avatar, ""),
    },
    address: asString(item.address, "-"),
  };
}

function extractInquiryList(payload: unknown): InquiryApiRecord[] {
  if (Array.isArray(payload)) {
    return payload as InquiryApiRecord[];
  }

  const wrapped = payload as { data?: unknown; inquiries?: unknown };
  if (Array.isArray(wrapped?.data)) {
    return wrapped.data as InquiryApiRecord[];
  }
  if (Array.isArray(wrapped?.inquiries)) {
    return wrapped.inquiries as InquiryApiRecord[];
  }

  return [];
}

async function loadInquiries() {
  const requestId = ++loadRequestId;
  const append = pagination.value.current_page > 0 && isLoadingMore.value;
  const nextPage = append ? pagination.value.current_page + 1 : 1;

  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }
  errorMessage.value = "";

  try {
    const params: InquiryQueryParams = {
      per_page: "10",
      page: String(nextPage),
    };
    const query = searchQuery.value.trim();
    if (query) params.search = query;
    if (activeTab.value !== "all") params.status = activeTab.value;
    if (isVendorMode()) {
      params.is_vendor = "true";
    }

    const response = await fetchVendorInquiries(params);
    if (requestId !== loadRequestId) return;

    const normalized = extractInquiryList(response).map(normalizeInquiry);
    inquiries.value = append ? [...inquiries.value, ...normalized] : normalized;

    const meta = extractMeta(response);
    if (meta) {
      pagination.value = meta;
      hasMore.value = meta.current_page < meta.last_page;
    } else {
      pagination.value = {
        current_page: nextPage,
        last_page: nextPage,
        per_page: 10,
        total: inquiries.value.length,
      };
      hasMore.value = false;
    }
  } catch (error) {
    if (requestId !== loadRequestId) return;
    console.error("Failed to load inquiries", error);
    errorMessage.value = "Unable to load inquiries right now.";
    if (!append) {
      inquiries.value = [];
      pagination.value = { current_page: 1, last_page: 1, per_page: 10, total: 0 };
      hasMore.value = false;
    }
  } finally {
    if (requestId !== loadRequestId) return;
    if (append) {
      isLoadingMore.value = false;
    } else {
      isLoading.value = false;
    }
  }
}

function extractMeta(payload: unknown): InquiryListMeta | null {
  const wrapped = payload as { meta?: Record<string, unknown> };
  if (!wrapped?.meta) return null;
  const meta = wrapped.meta;
  const currentPage = Number(meta.current_page || 1);
  const lastPage = Number(meta.last_page || 1);
  const perPage = Number(meta.per_page || 10);
  const total = Number(meta.total || 0);

  return {
    current_page: Number.isNaN(currentPage) ? 1 : currentPage,
    last_page: Number.isNaN(lastPage) ? 1 : lastPage,
    per_page: Number.isNaN(perPage) ? 10 : perPage,
    total: Number.isNaN(total) ? 0 : total,
  };
}

function handleOpen(id: number) {
  router.push({ name: "vendor.inquiry.detail", params: { id } });
}

function statusClass(status: string) {
  return `card-status--${status.toLowerCase()}`;
}

function loadMore() {
  if (isLoadingMore.value || !hasMore.value) return;
  isLoadingMore.value = true;
  loadInquiries();
}

onMounted(loadInquiries);

watch(activeTab, () => {
  isLoadingMore.value = false;
  loadInquiries();
});

watch(searchQuery, () => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    isLoadingMore.value = false;
    loadInquiries();
  }, 350);
});

onBeforeUnmount(() => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
});
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
}

.fetching-state {
  padding: 8px 0 4px;
}

.inquiry-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.inquiry-card {
  background: #fff;
  border-radius: var(--radius-lg);
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
}

.inquiry-card:focus-visible {
  outline: 2px solid #f97316;
  outline-offset: 2px;
}

.card-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.card-icon {
  width: 42px;
  height: 42px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  overflow: hidden;
}

.card-icon--peach {
  background: #fff1f2;
  color: #f97316;
}

.card-icon--sun {
  background: #fef9c3;
  color: #f97316;
}

.card-icon--sky {
  background: #e0f2fe;
  color: #0ea5e9;
}

.card-icon--amber {
  background: #fef3c7;
  color: #f59e0b;
}

.card-icon--mint {
  background: #dcfce7;
  color: #16a34a;
}

.card-copy {
  min-width: 0;
}

.card-title {
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  color: var(--color-title);
}

.card-address {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  margin-bottom: 10px;
  font-size: var(--font-size-default);
  color: var(--color-subtitle-light);
}

.card-address__icon {
  opacity: 0.9;
}

.card-address__text {
  white-space: normal;
  word-break: break-word;
}

.card-id {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-theme);
}

.card-subtitle {
  margin: 00;
  font-size: var(--font-size-default);
  color: #313a45;
}

.card-meta {
  margin-top: 6px;
  font-size: 13px;
  color: var(--color-subtitle-light);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-theme);
  font-weight: 600;
}

.card-status {
  font-size: var(--font-size-default);
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
  text-transform: capitalize;
}

.card-status--new {
  color: #2563eb;
  background: #dbeafe;
}

.card-status--quoted {
  color: #c2410c;
  background: #ffedd5;
}

.card-status--accepted {
  color: #15803d;
  background: #dcfce7;
}

.card-status--converted {
  color: #92400e;
  background: #fef3c7;
}
</style>
